// The reporting dashboards' two SAVE-MODEL controls, shared by both pages.
//
// Personal saving stays automatic and invisible — every drag, toggle and config
// change already persists for the signed-in user. What this adds is the shared
// layer around it:
//
//   · "Reset"                — everyone. Drops YOUR view for this dashboard, so
//                              you fall back to the team default (or the
//                              built-in if no admin has set one).
//   · "Save as team default" — admins only. Makes the current view the org-wide
//                              default for this dashboard.
//
// ⚠️ DELIBERATELY TWO CONTROLS. This is not a named-views / multi-preset system;
// that model was considered and not chosen. Everything else about saving stays
// automatic, so there is no "save for me" button to explain.
//
// ⚠️ FILTERS ARE NOT PART OF A SAVED VIEW — not the date range, not brand / AM /
// group, not the name-contains boxes. Those are per-session query state: baking
// "last 30 days, brand X" into the team default would hand every colleague a
// stale question rather than a useful layout. Only the display config (the
// canvas arrangement, column selections, levels, charts, widgets, styles) rides
// `gridPreferences`.
//
// ⚠️ THE LABEL IS NOT DECORATION. "Reset" reverts to the team default, which is
// a silent no-op for someone who has never customised — so the control says
// which view they are on and disables itself when there is nothing to revert.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { hasPersonalGridPreference } from '../../lib/gridPreferenceResolution';
import type { CurrentUser, GridPreferences } from '../../types/user';
import { clearMyPreferences, saveTeamPreferences } from '../accounts';
import { useCurrentUser } from '../auth';

export function ReportingViewActions({
  gridKey,
  preferences,
  onReset,
  className,
}: {
  /** Which dashboard's view this governs ('reporting-charts' / 'google-ads'). */
  gridKey: string;
  /** The CURRENT view, already encoded — what "Save as team default" would store. */
  preferences: GridPreferences;
  /**
   * Re-seed the page from the freshly-resolved user after a Reset.
   *
   * ⚠️ REQUIRED, not optional. Each page seeds its view state ONCE from
   * `user.gridPreferences` (deliberately — re-applying on every /me refetch
   * would stomp a selection whose save was still in flight), so after clearing
   * the personal pref the page would otherwise keep showing the view it just
   * discarded until a reload.
   */
  onReset: (user: CurrentUser) => void;
  className?: string;
}): React.JSX.Element {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const isAdmin = user.role === 'admin';
  const hasPersonal = hasPersonalGridPreference(user.gridPreferenceSources, gridKey);

  const reset = useMutation({
    mutationFn: () => clearMyPreferences(gridKey),
    onSuccess: (next) => {
      setConfirmReset(false);
      qc.setQueryData(['me'], next);
      onReset(next);
    },
    onError: (err) => {
      setConfirmReset(false);
      console.error('Failed to reset the dashboard view', err);
    },
  });

  const saveTeam = useMutation({
    mutationFn: () => saveTeamPreferences(gridKey, preferences),
    onSuccess: () => {
      setConfirmOpen(false);
      // Re-fetch /me so THIS admin immediately sees the source label settle —
      // they may have had no personal pref, in which case they are now looking
      // at the team default they just set.
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => {
      setConfirmOpen(false);
      console.error('Failed to save the team default', err);
    },
  });

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-xs text-muted-foreground">
        {hasPersonal ? 'Your view' : 'Team default'}
      </span>

      <button
        type="button"
        onClick={() => setConfirmReset(true)}
        disabled={!hasPersonal || reset.isPending}
        title={
          hasPersonal
            ? 'Discard your own view of this dashboard and go back to the team default'
            : 'You are already on the team default — nothing to reset'
        }
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground',
          hasPersonal ? 'hover:bg-accent hover:text-foreground' : 'cursor-not-allowed opacity-40',
        )}
      >
        <RotateCcw className="h-3 w-3" aria-hidden />
        {reset.isPending ? 'Resetting…' : 'Reset'}
      </button>

      {/* Admins only. Staff never see it — a control that would 403 is worse
          than one that isn't there. */}
      {isAdmin && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          title="Make this arrangement the default for everyone who hasn’t customised their own"
        >
          <Users className="mr-1 h-3.5 w-3.5" aria-hidden />
          Save as team default
        </Button>
      )}

      {/* ⚠️ Reset CONFIRMS, because it now discards more than an arrangement.
          Before the save model it only put the panels back; it now drops the
          user's whole personal view of this dashboard — including any widgets
          they built — so the dialog says exactly that rather than letting
          someone discover it afterwards. */}
      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to the team default?</DialogTitle>
            <DialogDescription>
              This discards your own layout, column choices and widgets for this dashboard and
              puts you back on the team default. You’ll keep inheriting it — including any later
              changes an admin makes — until you customise again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => reset.mutate()} disabled={reset.isPending}>
              {reset.isPending ? 'Resetting…' : 'Reset my view'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as team default?</DialogTitle>
            <DialogDescription>
              This will become the default for everyone who hasn’t customised their own view of
              this dashboard. Anyone who has will keep theirs until they press Reset. Filters
              (date range, brand, account) aren’t saved — only the layout, columns and widgets.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => saveTeam.mutate()} disabled={saveTeam.isPending}>
              {saveTeam.isPending ? 'Saving…' : 'Save as team default'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
